import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  importLibrary,
  setOptions,
} from '@googlemaps/js-api-loader';

import storesData from './data/stores.json';
import markerIcon from './images/ico-store-marker.svg';
import './StoreLocator.scss';

type Store = {
  id: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  lat: number;
  lng: number;
};

type Location = {
  postalCode: string;
  city: string;
};

type UserPosition = {
  lat: number;
  lng: number;
};

type StoreWithDistance = Store & {
  distance?: number;
};

const stores = storesData as Store[];

const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const earthRadius = 6371;

  const lat1Rad = (lat1 * Math.PI) / 180;
  const lat2Rad = (lat2 * Math.PI) / 180;

  const deltaLat = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLng = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1Rad) *
      Math.cos(lat2Rad) *
      Math.sin(deltaLng / 2) ** 2;

  const c =
    2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadius * c;
};

const formatDistance = (distance: number): string => {
  if (distance < 10) {
    return `${distance.toFixed(1)} km`;
  }

  return `${Math.round(distance)} km`;
};

const MAP_PRELOAD_MARGIN = '300px';

const StoreLocator: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const mapRef = useRef<HTMLDivElement | null>(null);

  const [shouldLoadMap, setShouldLoadMap] = useState(false);

  const mapInstanceRef =
    useRef<google.maps.Map | null>(null);

  const markersRef =
    useRef<google.maps.Marker[]>([]);

  const userMarkerRef =
    useRef<google.maps.Marker | null>(null);

  const infoWindowRef =
    useRef<google.maps.InfoWindow | null>(null);

  const [searchValue, setSearchValue] = useState('');

  const [selectedLocation, setSelectedLocation] =
    useState<Location | null>(null);

  const [userPosition, setUserPosition] =
    useState<UserPosition | null>(null);

  const [isLocating, setIsLocating] =
    useState(false);

  const [geolocationError, setGeolocationError] =
    useState<string | null>(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const locations = useMemo<Location[]>(() => {
    const uniqueLocations = new Map<string, Location>();

    stores.forEach((store) => {
      const key = `${store.postalCode}-${store.city}`;

      if (!uniqueLocations.has(key)) {
        uniqueLocations.set(key, {
          postalCode: store.postalCode,
          city: store.city,
        });
      }
    });

    return Array.from(uniqueLocations.values());
  }, []);
  const suggestions = useMemo<Location[]>(() => {
    const query = searchValue.trim().toLowerCase();

    if (!query) {
      return [];
    }

    return locations
      .filter((location) => {
        return (
          location.postalCode
            .toLowerCase()
            .includes(query) ||
          location.city
            .toLowerCase()
            .includes(query)
        );
      })
      .slice(0, 8);
  }, [searchValue, locations]);
  const filteredStores = useMemo<
    StoreWithDistance[]
  >(() => {
    if (!selectedLocation) {
      return [];
    }

    return stores
      .filter(
        (store) =>
          store.postalCode ===
            selectedLocation.postalCode &&
          store.city === selectedLocation.city
      )
      .map((store) => ({
        ...store,
        distance: userPosition
          ? calculateDistance(
              userPosition.lat,
              userPosition.lng,
              store.lat,
              store.lng
            )
          : undefined,
      }))
      .sort((a, b) => {
        if (!userPosition) {
          return 0;
        }

        return a.distance - b.distance;
      });
  }, [selectedLocation, userPosition]);
  const nearbyStores = useMemo<
    StoreWithDistance[]
  >(() => {
    if (!userPosition) {
      return [];
    }

    return stores
      .map((store) => ({
        ...store,
        distance: calculateDistance(
          userPosition.lat,
          userPosition.lng,
          store.lat,
          store.lng
        ),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 10);
  }, [userPosition]);
  const storesToDisplay = selectedLocation
    ? filteredStores
    : nearbyStores;
  const applyDesktopMapOffset = (
    map: google.maps.Map
  ) => {
    if (!mapRef.current) {
      return;
    }

    const isDesktop = window.matchMedia(
      '(min-width: 769px)'
    ).matches;

    if (!isDesktop) {
      return;
    }

    const mapWidth =
      mapRef.current.getBoundingClientRect().width;

    // Move the selected point to the center of the visible right half.
    map.panBy(-(mapWidth * 0.25), 0);
  };
  const panToLocation = (
    position: google.maps.LatLngLiteral,
    zoom?: number
  ) => {
    const map = mapInstanceRef.current;

    if (!map) {
      return;
    }

    if (zoom !== undefined) {
      map.setZoom(zoom);
    }

    map.panTo(position);

    google.maps.event.addListenerOnce(
      map,
      'idle',
      () => {
        applyDesktopMapOffset(map);
      }
    );
  };
  useEffect(() => {
    const container = containerRef.current;

    if (!container || shouldLoadMap) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      setShouldLoadMap(true);

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoadMap(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: `${MAP_PRELOAD_MARGIN} 0px`,
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [shouldLoadMap]);
  useEffect(() => {
    if (!shouldLoadMap) {
      return;
    }

    const initMap = async () => {
      if (!mapRef.current) {
        return;
      }

      const apiKey =
        process.env.GATSBY_GOOGLE_MAPS_API_KEY;

      if (!apiKey) {
        console.error(
          'GATSBY_GOOGLE_MAPS_API_KEY is not defined.'
        );

        return;
      }

      try {
        setOptions({
          key: apiKey,
          v: 'weekly',
        });

        await importLibrary('maps');
        await importLibrary('marker');

        if (!mapRef.current) {
          return;
        }

        mapInstanceRef.current =
          new google.maps.Map(mapRef.current, {
            center: {
              lat: 46.603354,
              lng: 1.888334,
            },
            zoom: 6,

            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            zoomControl: true,

            gestureHandling: 'greedy',
          });

        infoWindowRef.current =
          new google.maps.InfoWindow();

        setMapLoaded(true);
      } catch (error) {
        console.error(
          'Failed to initialize Google Maps:',
          error
        );
      }
    };

    initMap();
  }, [shouldLoadMap]);
  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      setGeolocationError(
        'La géolocalisation n’est pas prise en charge par votre navigateur.'
      );

      return;
    }

    setIsLocating(true);
    setGeolocationError(null);
    setSelectedLocation(null);
    setSearchValue('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserPosition({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });

        setGeolocationError(null);
        setIsLocating(false);
      },
      (error) => {
        console.error('Geolocation error:', {
          code: error.code,
          message: error.message,
        });

        setIsLocating(false);

        switch (error.code) {
          case 1:
            setGeolocationError(
              'Nous ne pouvons pas accéder à votre position. Vérifiez les autorisations de géolocalisation dans les paramètres de votre navigateur, puis réessayez.'
            );
            break;

          case 2:
            setGeolocationError(
              'Votre position est actuellement indisponible.'
            );
            break;

          case 3:
            setGeolocationError(
              'Impossible de déterminer votre position dans le délai imparti.'
            );
            break;

          default:
            setGeolocationError(
              `Impossible de déterminer votre position. Code d’erreur : ${error.code}`
            );
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      }
    );
  };
  useEffect(() => {
    if (
      !mapLoaded ||
      !mapInstanceRef.current ||
      !userPosition
    ) {
      return;
    }

    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
    }

    userMarkerRef.current =
      new google.maps.Marker({
        position: userPosition,
        map: mapInstanceRef.current,
        title: 'Votre position',
        zIndex: 1000,
      });
  }, [mapLoaded, userPosition]);
  useEffect(() => {
    if (
      !mapLoaded ||
      !mapInstanceRef.current
    ) {
      return;
    }

    const map = mapInstanceRef.current;
    markersRef.current.forEach((marker) => {
      marker.setMap(null);
    });

    markersRef.current = [];
    infoWindowRef.current?.close();

    if (!storesToDisplay.length) {
      return;
    }

    const bounds =
      new google.maps.LatLngBounds();

    storesToDisplay.forEach((store) => {
      const position = {
        lat: store.lat,
        lng: store.lng,
      };

      const marker = new google.maps.Marker({
        position,
        map,
        title: store.name,

        icon: {
          url: markerIcon,
          scaledSize: new google.maps.Size(
            40,
            40
          ),
        },
      });
      marker.addListener('click', () => {
        if (!infoWindowRef.current) {
          return;
        }

        const directionsUrl =
          'https://www.google.com/maps/dir/?api=1' +
          `&destination=${encodeURIComponent(
            `${store.lat},${store.lng}`
          )}`;

        const distanceContent =
          userPosition && store.distance !== undefined
            ? `
                <div class="store-locator__info-window-distance">
                  ${formatDistance(
                    store.distance
                  )}
                </div>
              `
            : '';

        const content = `
          <div class="store-locator__info-window">
            <div class="store-locator__info-window-name">
              ${store.name}
            </div>

            <div class="store-locator__info-window-address">
              ${store.address}
            </div>

            <div class="store-locator__info-window-location">
              ${store.city} ${store.postalCode}
            </div>

            ${distanceContent}

            <a
              class="store-locator__info-window-directions"
              href="${directionsUrl}"
              target="_blank"
              rel="noopener noreferrer"
            >
              Itinéraire
            </a>
          </div>
        `;

        infoWindowRef.current.setContent(
          content
        );

        infoWindowRef.current.open({
          map,
          anchor: marker,
        });
      });

      markersRef.current.push(marker);

      bounds.extend(position);
    });
    if (
      userPosition &&
      !selectedLocation
    ) {
      panToLocation(userPosition, 11);

      return;
    }
    if (storesToDisplay.length === 1) {
      const position = {
        lat: storesToDisplay[0].lat,
        lng: storesToDisplay[0].lng,
      };

      panToLocation(position, 15);

      return;
    }
    const mapWidth =
      mapRef.current?.getBoundingClientRect().width ?? 0;
    const isDesktop = window.matchMedia(
      '(min-width: 769px)'
    ).matches;

    map.fitBounds(bounds, {
      top: 80,
      right: 80,
      bottom: 80,
      left: isDesktop
        ? mapWidth * 0.5 + 80
        : 80,
    });
  }, [
    storesToDisplay,
    mapLoaded,
    selectedLocation,
    userPosition,
  ]);
  const handleLocationSelect = (
    location: Location
  ) => {
    setSearchValue(
      `${location.postalCode} ${location.city}`
    );

    setSelectedLocation(location);
    setGeolocationError(null);
  };
  const handleSearch = () => {
    if (!suggestions.length) {
      return;
    }

    handleLocationSelect(suggestions[0]);
  };
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div
      ref={containerRef}
      className="store-locator"
      id="storeLocator"
    >
      <div className="store-locator__search">
        <div className='store-locator__search-holder'>
          <h2>Trouve un distributeur de préservatifs près de chez toi</h2>
          <p>Utilise la carte interactive pour trouver le distributeur Durex sans contact le plus proche de chez toi.</p>
          <div className='store-locator__input-area'>
            <div className='store-locator__input-area-row'>
              <input
                type="text"
                value={searchValue}
                placeholder="Ville, code postal..."
                onChange={(event) =>
                  setSearchValue(event.target.value)
                }
                onKeyDown={handleKeyDown}
              />

              <button
                type="button"
                onClick={handleSearch}
                aria-label="Rechercher"
              >
                Rechercher
              </button>
            </div>

            {suggestions.length > 0 && (
              <div className='store-locator__suggestions'>
                <ul>
                  {suggestions.map((location) => (
                    <li
                      key={`${location.postalCode}-${location.city}`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          handleLocationSelect(location)
                        }
                      >
                        <span>{location.city} </span>
                        <span>
                          {location.postalCode}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleGeolocation}
            disabled={isLocating}
            aria-label="me géolocaliser​"
            className="btn-geolocate-me"
          >
            {isLocating
              ? 'Localisation en cours...'
              : 'me géolocaliser​'}
          </button>

          {geolocationError && (
            <div className="store-locator__error">
              {geolocationError}
            </div>
          )}
        </div>
      </div>

      <div
        ref={mapRef}
        className="store-locator__map"
      />
    </div>
  );
};

export default StoreLocator;